import { documentToReactComponents, Options, RenderMark } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES, MARKS } from '@contentful/rich-text-types';
import { useTranslation } from 'react-i18next';
import styles from './renderer.module.scss';

export const H1 = ({ children }) => <h1 className={styles['ds-heading-h1']}>{replaceNewLinesInChildren(children)}</h1>;
export const H2 = ({ children }) => <h2 className={styles['ds-heading-h2']}>{replaceNewLinesInChildren(children)}</h2>;
export const H3 = ({ children }) => <h3 className={styles['ds-heading-h3']}>{replaceNewLinesInChildren(children)}</h3>;
export const H4 = ({ children }) => <h4 className={styles['ds-heading-h4']}>{replaceNewLinesInChildren(children)}</h4>;
export const Text = ({ children }) => <p className={styles['ds-text']}>{replaceNewLinesInChildren(children)}</p>;
export const Bold = ({ children }) => <span className={styles['ds-bold']}>{replaceNewLinesInChildren(children)}</span>;
export const Italic = ({ children }) => <span className={styles['ds-italic']}>{replaceNewLinesInChildren(children)}</span>;
export const Underline = ({ children }) => <span className={styles['ds-text-underline']}>{replaceNewLinesInChildren(children)}</span>;

export const ListItem = ({ children }) => <li className="">{replaceNewLinesInChildren(children)}</li>;
export const OlList = ({ children }) => (
  <ol className="my-6 list-inside list-decimal stihl-text-body-default-reg">{replaceNewLinesInChildren(children)}</ol>
);
export const UlList = ({ children }) => (
  <ul className="my-6 list-inside list-disc stihl-text-body-default-reg">{replaceNewLinesInChildren(children)}</ul>
);
export const Quote = ({ children }) => (
  <blockquote className="my-6 italic stihl-text-body-default-reg">"{replaceNewLinesInChildren(children)}"</blockquote>
);
export const HR = () => <hr className="mb-6 mt-4" />;
export const Hyperlink: React.FC<React.PropsWithChildren<{ uri: string }>> = ({ children, uri }) => {
  const { t } = useTranslation('shared');
  let targetType = '_self';
  let rel: string | undefined = undefined;
  if (!uri.startsWith(`https://app.stihl.com`)) {
    targetType = '_blank';
    rel = 'noopener';
  }

  return (
    <a className={styles.link} href={uri} target={targetType} rel={rel}>
      {replaceNewLinesInChildren(children)}
      {targetType === '_blank' && <span className="sr-only">{t('shared_a11y_hint_opensInNewTab')}</span>}
    </a>
  );
};

export const defaultRenderMark: RenderMark = {
  [MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
  [MARKS.ITALIC]: (text) => <Italic>{text}</Italic>,
  [MARKS.UNDERLINE]: (text) => <Underline>{text}</Underline>,
};

export const defaultRenderNode = {
  [BLOCKS.HEADING_1]: (node, children) => <H1>{children}</H1>,
  [BLOCKS.HEADING_2]: (node, children) => <H2>{children}</H2>,
  [BLOCKS.HEADING_3]: (node, children) => <H3>{children}</H3>,
  [BLOCKS.HEADING_4]: (node, children) => <H4>{children}</H4>,
  [BLOCKS.PARAGRAPH]: (node, children) => {
    return <Text>{children}</Text>;
  },
  [BLOCKS.OL_LIST]: (node, children) => <OlList>{children}</OlList>,
  [BLOCKS.UL_LIST]: (node, children) => <UlList>{children}</UlList>,
  [BLOCKS.HR]: () => <HR />,
  [INLINES.HYPERLINK]: (node, children) => <Hyperlink uri={node.data.uri}>{children}</Hyperlink>,
};

export const defaultRenderOptions: Options = {
  renderMark: defaultRenderMark,
  renderNode: {
    ...defaultRenderNode,
    [BLOCKS.LIST_ITEM]: (node) => {
      const UnTaggedChildren = documentToReactComponents(node as Document, {
        renderMark: defaultRenderMark,
        renderNode: {
          ...defaultRenderNode,
          [BLOCKS.PARAGRAPH]: (node, children) => children,
          [BLOCKS.LIST_ITEM]: (node, children) => children,
        },
      });

      return <ListItem>{UnTaggedChildren}</ListItem>;
    },
    [BLOCKS.QUOTE]: (node) => {
      const UnTaggedChildren = documentToReactComponents(node as Document, {
        renderMark: defaultRenderMark,
        renderNode: {
          ...defaultRenderNode,
          [BLOCKS.PARAGRAPH]: (node, children) => children,
          [BLOCKS.QUOTE]: (node, children) => children,
        },
      });

      return <Quote>{UnTaggedChildren}</Quote>;
    },
  },
};

const replaceNewLinesInChildren = (children) => {
  return Array.isArray(children) ? children.map(replaceNewLinesInText) : children;
};

const replaceNewLinesInText = (text) => {
  return typeof text === 'string'
    ? text.split('\n').reduce((children, textSegment, index) => {
        return [...children, index > 0 && <br key={index} />, textSegment];
      }, [])
    : text;
};
